/**
 * urls.js — Canonical URL builders.
 *
 * v3.99d.200 (Feb 2026) — Long-term SEO URL system. Internal callers
 * should NEVER hand-roll a `/listing/${id}` template literal again;
 * always route through `listingUrl(listing)` so the entire app uses
 * the same clean, slug-based path.
 *
 * Public structure:
 *   /shop                            — main buy-now catalog
 *   /shop/:breederSlug               — breeder's product catalog
 *   /shop/:breederSlug/:productSlug  — product detail page
 *
 * Fallback contract:
 *   If a listing is missing either slug (rare — backfill is lazy)
 *   we fall back to the legacy UUID URL `/listing/:id` which the
 *   frontend resolves and 301-redirects to the canonical URL.
 */

const SITE_ORIGIN = (
  process.env.REACT_APP_PUBLIC_SITE_URL
  || process.env.REACT_APP_FRONTEND_URL
  || "https://thecannaforge.com"
).replace(/\/$/, "");

/**
 * Return the clean public URL for a listing.
 * Accepts the full listing object — never just an id — because we need
 * both store_slug and product_slug to build the path.
 */
export function listingUrl(listing) {
  if (!listing) return "/shop";
  const bs = listing.store_slug;
  const ps = listing.product_slug;
  if (bs && ps) return `/shop/${bs}/${ps}`;
  if (listing.id) return `/listing/${listing.id}`;
  return "/shop";
}

/** Absolute URL for the same listing — used in OG tags, JSON-LD, emails. */
export function listingAbsoluteUrl(listing) {
  return `${SITE_ORIGIN}${listingUrl(listing)}`;
}

/** Breeder catalog page. */
export function breederShopUrl(vendor) {
  if (!vendor) return "/shop";
  const bs = vendor.store_slug || vendor.id;
  return `/shop/${bs}`;
}

/** Breeder profile page (separate from the shop catalog). */
export function breederProfileUrl(vendor) {
  if (!vendor) return "/breeders";
  const bs = vendor.store_slug || vendor.id;
  return `/breeder/${bs}`;
}

export { SITE_ORIGIN };
